import _ from "lodash";
import { DateTime } from "luxon";
import { MetricDto } from "../http";

function toChartDataWithNulls(metric: MetricDto) {
  if (metric.datasets.length === 0) {
    return [];
  }

  const uniqueRows = metric.datasets
    .flatMap((d) =>
      d.data.map((p) => {
        return {
          rowGuid: p.rowGuid,
          date: DateTime.fromISO(p.date, { zone: "utc" }),
        };
      }),
    )
    .filter((value, index, self) => self.findIndex((r) => r.rowGuid === value.rowGuid) === index)
    .sort((a, b) => a.date.toMillis() - b.date.toMillis()); // Ensure unique x values

  return metric.datasets.map((dataset) => {
    const dataMap = new Map<string, number>();

    dataset.data
      .filter((dataPoint) => dataPoint.value !== null)
      .forEach((dataPoint) => {
        dataMap.set(dataPoint.rowGuid, dataPoint.value!);
      });

    // Create chart data filling in missing row values with null
    const chartData = uniqueRows.map((row) => ({
      x: row.date.toFormat("M/d/yyyy"),
      y: dataMap.get(row.rowGuid) ?? null,
      rowGuid: row.rowGuid,
      hasAnnotation: metric.annotations.some((a) => a.rowGuid === row.rowGuid),
    }));

    return {
      ...dataset,
      chartData,
    };
  });
}

function toCsvData(metric: MetricDto): string {
  if (metric.datasets.length === 0) {
    return "";
  }

  // Iterate through datasets to figure out how many row/dates there are.
  const rows = metric.datasets.flatMap((dataset) =>
    dataset.data.map((data) => ({
      rowGuid: data.rowGuid,
      date: DateTime.fromISO(data.date, { zone: "utc" }),
    })),
  );

  // Make sure rows are unique by rowGuid.
  const uniqueSortedRows = _.uniqBy(rows, (row) => row.rowGuid).sort((a, b) => b.date.toMillis() - a.date.toMillis());

  // Build up CSV data as a map between rowGuid and CSV data row.
  const csvData = new Map<string, string[]>();

  // Special "headers" column will be the first row.
  csvData.set("headers", ["Date"]);

  // Push dates into each row array.
  uniqueSortedRows.forEach((row) => {
    csvData.set(row.rowGuid, [row.date.toFormat("yyyy-MM-dd")]);
  });

  metric.datasets
    .sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))
    .forEach((dataset) => {
      // Push titles into the headers array.
      csvData.get("headers")?.push(dataset.title);

      // Push data into the row arrays.
      dataset.data.forEach((data) => {
        csvData.get(data.rowGuid)?.push(data.value?.toString() ?? "");
      });
    });

  csvData.get("headers")?.push("Notes");

  // Push notes into the row arrays.
  metric.annotations.forEach((annotation) => {
    csvData.get(annotation.rowGuid)?.push(`"${annotation.text}"`);
  });

  // Comma-separate row values and add a new-line between each row.
  // The headers row should get mapped first due to Maps preserving insertion order.
  return [...csvData].map(([, value]) => value.join(",")).join("\n");
}

const MetricMapper = {
  toChartDataWithNulls,
  toCsvData,
};

export { MetricMapper };
